import { createContext, forwardRef, useContext, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

type RadioGroupContextValue = {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
};

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

export type RadioGroupProps = {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  error?: string;
  label?: string;
  children: ReactNode;
  className?: string;
};

export function RadioGroup({
  name,
  value,
  onChange,
  disabled,
  error,
  label,
  children,
  className = '',
}: RadioGroupProps) {
  return (
    <RadioGroupContext.Provider value={{ name, value, onChange, disabled, error: !!error }}>
      <div role="radiogroup" aria-label={label} className={cn('flex flex-col gap-1', className)}>
        {label && (
          <span className="text-sm font-medium text-gray-300 mb-1">{label}</span>
        )}
        <div className="flex flex-col gap-2">
          {children}
        </div>
        {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
      </div>
    </RadioGroupContext.Provider>
  );
}

export type RadioProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> & {
  label?: string;
  description?: string;
  value: string;
};

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ label, description, value, className = '', id, disabled: localDisabled, ...props }, ref) => {
    const context = useContext(RadioGroupContext);
    const inputId = id || `radio-${value}`;

    const isDisabled = localDisabled || context?.disabled;
    const isChecked = context?.value === value;
    const hasError = context?.error;

    const handleChange = () => {
      context?.onChange?.(value);
    };

    return (
      <label
        htmlFor={inputId}
        className={cn(
          'group flex items-start gap-3 cursor-pointer',
          isDisabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
      >
        <div className="relative flex items-center justify-center mt-0.5">
          <input
            ref={ref}
            id={inputId}
            type="radio"
            name={context?.name}
            value={value}
            checked={isChecked}
            onChange={handleChange}
            disabled={isDisabled}
            className="peer sr-only"
            {...props}
          />
          <div
            className={cn(
              'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-900',
              hasError
                ? 'border-red-500'
                : 'border-surface-600 peer-checked:border-brand-500 group-hover:border-surface-500',
            )}
          >
            <div
              className={cn(
                'w-2.5 h-2.5 rounded-full transition-all duration-200',
                isChecked
                  ? hasError ? 'bg-red-500 scale-100' : 'bg-brand-500 scale-100'
                  : 'bg-transparent scale-0',
              )}
            />
          </div>
        </div>
        {(label || description) && (
          <div className="flex flex-col">
            {label && <span className="text-sm font-medium text-white">{label}</span>}
            {description && <span className="text-sm text-gray-400">{description}</span>}
          </div>
        )}
      </label>
    );
  },
);

Radio.displayName = 'Radio';
